/**
 * The review step's "how do you want to confirm" block (owner, 2026-10-21, layout B of the
 * mockup round).
 *
 * The salon's connected channels (a bot switched to automatic, the salon's official WhatsApp,
 * or the salon's own number: inbox README §5.1) let the client confirm the visit themselves in
 * the chat; they come first, under «yourself, in a messenger». Below them sits «or the salon
 * contacts you»: the phone call and the channels the admin answers by hand (Viber, a custom
 * name the salon added). The server sends the channels in the salon's order (Settings ›
 * Messenger).
 *
 * The official WhatsApp (kind "official", inbox plan INB4) needs no step from the client: the
 * messages come from the salon's number to the one she books with, with buttons — and picking
 * it is her consent to them (inbox README §12.3), so the hint says so.
 */
import { MessageCircleMore, Phone, PhoneCall } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** one entry of /public/company/settings' messenger list: `auto` = confirms in the chat;
 *  `kind` = how — the salon's bot, the salon's official WhatsApp, or the salon's own number;
 *  `label` = a custom name */
export type ConfirmChannel = { id: string; auto: boolean; kind?: "bot" | "official" | "own"; label?: string };

type Props = {
  /** in the salon's order */
  channels: ConfirmChannel[];
  /** the salon offers the phone call */
  phone: boolean;
  confirmMethod: 1 | 2;
  messengerChannel?: string;
  onChangeConfirmMethod: (m: 1 | 2) => void;
  onChangeMessengerChannel: (ch: string) => void;
};

const BRAND: Record<string, { color: string; glyph: ReactNode }> = {
  whatsapp: { color: "#25D366", glyph: <Phone className="h-3.5 w-3.5" strokeWidth={2.5} /> },
  telegram: { color: "#229ED9", glyph: <PaperPlane className="h-3.5 w-3.5" /> },
  viber: { color: "#7360F2", glyph: <PhoneCall className="h-3.5 w-3.5" strokeWidth={2.5} /> },
  max: { color: "#5B4CF5", glyph: <span className="text-[11px] font-bold leading-none">M</span> },
  vk: { color: "#0077FF", glyph: <span className="text-[9px] font-bold leading-none">VK</span> },
};

export function Mark({ id, size = "md" }: { id: string; size?: "sm" | "md" }) {
  const brand = BRAND[id] ?? { color: "#64748b", glyph: <MessageCircleMore className="h-3.5 w-3.5" /> };
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-white ${size === "sm" ? "h-5 w-5" : "h-7 w-7"}`}
      style={{ backgroundColor: brand.color }}
      aria-hidden
    >
      {brand.glyph}
    </span>
  );
}

/** a channel's display name: the salon's own label, else the messenger's name */
export function useChannelName() {
  const { t } = useTranslation();
  return (ch: { id: string; label?: string }) =>
    ch.label ||
    ({
      telegram: t("widget.messenger.channel_telegram", "Telegram"),
      whatsapp: t("widget.messenger.channel_whatsapp", "WhatsApp"),
      viber: t("widget.messenger.channel_viber", "Viber"),
      max: t("widget.messenger.channel_max", "MAX"),
      vk: t("widget.messenger.channel_vk", "VK"),
    } as Record<string, string>)[ch.id] ||
    ch.id;
}

export default function ConfirmChannels({
  channels, phone, confirmMethod, messengerChannel, onChangeConfirmMethod, onChangeMessengerChannel,
}: Props) {
  const { t } = useTranslation();
  const name = useChannelName();
  const connected = channels.filter((c) => c.auto);
  const manual = channels.filter((c) => !c.auto);
  if (!channels.length) {
    // nothing to choose: the salon calls
    return phone ? (
      <div>
        <SectionLabel>{t("widget.overview.confirmation", "Booking confirmation")}</SectionLabel>
        <Hint>{t("widget.confirm.hint_phone", "The salon will call you to confirm the visit.")}</Hint>
      </div>
    ) : null;
  }

  const selected = confirmMethod === 2 || !phone ? channels.find((c) => c.id === messengerChannel) ?? channels[0] : undefined;
  const choose = (m: 1 | 2, ch?: string) => {
    onChangeConfirmMethod(m);
    if (ch) onChangeMessengerChannel(ch);
  };
  /** what happens after booking on the selected channel */
  const hint = !selected
    ? t("widget.confirm.hint_phone", "The salon will call you to confirm the visit.")
    : !selected.auto
      ? t("widget.messenger.picker_hint_manual", "The salon will contact you in {{messenger}} to confirm.", { messenger: name(selected) })
      : selected.kind === "official"
        ? t("widget.confirm.hint_official", "The confirmation and the reminder will come to your number in {{messenger}} from the salon, with a Confirm button.", { messenger: name(selected) })
        : selected.kind === "own"
          ? selected.id === "max"
            // a MAX link opens the profile only: the client types the code
            ? t("widget.confirm.hint_own_typed", "After booking, send us the code in {{messenger}}. The reminder comes there too, and one reply confirms.", { messenger: name(selected) })
            : t("widget.confirm.hint_own", "After booking, write to us in {{messenger}}: the message with your code is already typed. The reminder comes there too, and one reply confirms.", { messenger: name(selected) })
          : t("widget.confirm.hint_bot", "After booking, open the salon's bot in {{messenger}}: the confirmation and the reminder arrive there, with a Confirm button.", { messenger: name(selected) });

  return (
    <div>
      <SectionLabel>{t("widget.confirm.list_title", "How would you like to confirm?")}</SectionLabel>
      {connected.length > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-1.5">
          <div className="flex items-center justify-between gap-2 px-2 pb-1 pt-1">
            <span className="text-xs font-semibold">{t("widget.confirm.group_self", "Yourself, in a messenger")}</span>
            <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground">
              {t("widget.confirm.fastest", "fastest")}
            </span>
          </div>
          <div className="space-y-1">
            {connected.map((ch) => (
              <Row key={ch.id} on={selected?.id === ch.id} onClick={() => choose(2, ch.id)} icon={<Mark id={ch.id} />}
                   title={name(ch)} filled
                   sub={ch.kind === "own"
                     ? t("widget.confirm.row_own", "the salon's number · reply “1” to the reminder")
                     : ch.kind === "official"
                       ? t("widget.confirm.row_official", "from the salon's number · a Confirm button")
                       : t("widget.confirm.row_bot", "the salon's bot · a Confirm button")} />
            ))}
          </div>
        </div>
      )}
      {(phone || manual.length > 0) && (
        <div className={connected.length > 0 ? "mt-3" : ""}>
          {connected.length > 0 && (
            <div className="px-2 pb-1 text-xs text-muted-foreground">{t("widget.confirm.group_salon", "Or the salon contacts you")}</div>
          )}
          <div className="space-y-1">
            {phone && (
              <Row on={!selected} onClick={() => choose(1)} icon={<PhoneMark />} title={t("widget.common.phone", "Phone")}
                   sub={t("widget.confirm.row_phone", "a call from the administrator")} />
            )}
            {manual.map((ch) => (
              <Row key={ch.id} on={selected?.id === ch.id} onClick={() => choose(2, ch.id)} icon={<Mark id={ch.id} />}
                   title={name(ch)} sub={t("widget.confirm.row_manual", "the administrator writes to you")} />
            ))}
          </div>
        </div>
      )}
      <Hint className="mt-2">{hint}</Hint>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="mb-2 block text-xs text-muted-foreground">{children}</div>;
}

function Hint({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <p className={`text-xs italic text-muted-foreground/70 ${className}`}>{children}</p>;
}

function Row({ on, onClick, icon, title, sub, filled = false }: {
  on: boolean; onClick: () => void; icon: ReactNode; title: string; sub: string; filled?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left ${on ? "border-primary ring-1 ring-primary" : "border-border"} ${filled ? "bg-card" : ""}`}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs leading-snug text-muted-foreground">{sub}</span>
      </span>
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${on ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
        {on && <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
      </span>
    </button>
  );
}

function PhoneMark() {
  return (
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground" aria-hidden>
      <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
    </span>
  );
}

/** Telegram's paper plane (lucide has no brand icons) */
export function PaperPlane({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M21.9 4.3 18.7 19.4c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-5 9.1-8.2c.4-.4-.1-.6-.6-.2L6.2 13 1.4 11.5c-1-.3-1-1 .2-1.5L20.6 2.8c.9-.3 1.6.2 1.3 1.5z" />
    </svg>
  );
}
